// TemplateFieldRenderer.jsx
// Type-switch compartido para renderizar un campo de `form_templates` tanto
// en la vista previa del admin (FormBuilder.jsx, modo disabled) como en el
// formulario real que responde la clienta (FitEvolutionUpdate.jsx).
// Mantener este único renderer evita que ambas vistas se desincronicen
// (ticket #265569: preguntas agregadas en el admin no se veían en el cliente).
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

export default function TemplateField({ field, value, onChange, disabled = false, 'data-testid': testId }) {
  const scaleMin = field.scaleMin ?? 1;
  const scaleMax = field.scaleMax ?? 10;
  const scaleRange = Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => scaleMin + i);
  const options = field.options || [];
  const emit = (v) => { if (!disabled && onChange) onChange(v); };

  return (
    <div className="space-y-2" data-testid={testId}>
      <Label className="text-white/80 block text-sm">
        {field.label} {field.required && <span className="text-red-400">*</span>}
      </Label>

      {field.type === 'text' && (
        <Input value={value || ''} onChange={(e) => emit(e.target.value)} placeholder={field.placeholder} className="bg-black/40 border-white/15 text-white" disabled={disabled} />
      )}

      {field.type === 'email' && (
        <Input type="email" value={value || ''} onChange={(e) => emit(e.target.value)} placeholder={field.placeholder || 'correo@ejemplo.com'} className="bg-black/40 border-white/15 text-white" disabled={disabled} />
      )}

      {field.type === 'phone' && (
        <Input type="tel" value={value || ''} onChange={(e) => emit(e.target.value)} placeholder={field.placeholder || '+57 300 123 4567'} className="bg-black/40 border-white/15 text-white" disabled={disabled} />
      )}

      {field.type === 'textarea' && (
        <Textarea value={value || ''} onChange={(e) => emit(e.target.value)} placeholder={field.placeholder} className="bg-black/40 border-white/15 text-white" disabled={disabled} />
      )}

      {field.type === 'number' && (
        <Input type="number" value={value ?? ''} onChange={(e) => emit(e.target.value)} placeholder={field.placeholder} className="bg-black/40 border-white/15 text-white" disabled={disabled} />
      )}

      {field.type === 'date' && (
        <Input type="date" value={value || ''} onChange={(e) => emit(e.target.value)} className="bg-black/40 border-white/15 text-white" disabled={disabled} />
      )}

      {field.type === 'time' && (
        <Input type="time" value={value || ''} onChange={(e) => emit(e.target.value)} className="bg-black/40 border-white/15 text-white" disabled={disabled} />
      )}

      {field.type === 'select' && (
        <select
          value={value || ''}
          onChange={(e) => emit(e.target.value)}
          className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm"
          disabled={disabled}
        >
          <option value="">{field.placeholder || '— Selecciona —'}</option>
          {options.map(o => <option key={o.id || o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}

      {field.type === 'radio' && (
        <div className="space-y-2">
          {options.map(o => (
            <label key={o.id || o.value} className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
              <input type="radio" checked={value === o.value} onChange={() => emit(o.value)} disabled={disabled} className="w-4 h-4" />
              {o.label}
            </label>
          ))}
        </div>
      )}

      {(field.type === 'checkbox' || field.type === 'multiselect') && (
        <div className="space-y-2">
          {options.map(o => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(o.value);
            return (
              <label key={o.id || o.value} className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => emit(checked ? arr.filter(v => v !== o.value) : [...arr, o.value])}
                  className="w-4 h-4"
                />
                {o.label}
              </label>
            );
          })}
        </div>
      )}

      {field.type === 'slider' && (
        <div className="space-y-2">
          <input
            type="range"
            min={field.min ?? 0}
            max={field.max ?? 100}
            step={field.step ?? 1}
            value={value ?? field.min ?? 0}
            onChange={(e) => emit(parseFloat(e.target.value))}
            disabled={disabled}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-pink-500"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>{field.min ?? 0}</span>
            <span>{field.max ?? 100}</span>
          </div>
        </div>
      )}

      {field.type === 'scale' && (
        <div className="space-y-2">
          <div className="flex justify-between gap-1">
            {scaleRange.map(num => (
              <button
                key={num}
                type="button"
                disabled={disabled}
                onClick={() => emit(num)}
                className={`flex-1 py-2 text-sm rounded border transition-colors ${value === num ? 'bg-pink-500/30 border-pink-400 text-white' : 'bg-white/10 border-white/20 text-gray-400 hover:bg-pink-500/20 hover:text-white'}`}
              >
                {num}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>{field.scaleLabels?.low || 'Bajo'}</span>
            <span>{field.scaleLabels?.high || 'Alto'}</span>
          </div>
        </div>
      )}

      {field.type === 'file' && (
        <div className="border-2 border-dashed border-white/20 rounded-lg p-4 text-center bg-white/5">
          <input
            type="file"
            disabled={disabled}
            accept={field.acceptedFileTypes || '.pdf,.jpg,.png,.jpeg'}
            onChange={(e) => emit(e.target.files?.[0]?.name || '')}
            className="text-white text-sm"
          />
          <p className="text-gray-500 text-xs mt-1">
            Formatos: {field.acceptedFileTypes || '.pdf,.jpg,.png'} | Máx: {field.maxFileSize || 5}MB
          </p>
        </div>
      )}

      {field.helpText && (
        <p className="text-xs text-gray-500">{field.helpText}</p>
      )}
    </div>
  );
}
